import React from 'react';
import FormActionButton from '../../components/FormActionButton';
import { UserData } from '../../UserContext';
import { formatAppointmentDateTime } from '../../lib/dateFormatting';
import Container from '../../components/Container';
import Heading2 from '../../components/Heading2';

interface AppointmentConfirmationViewModel {
  formattedDateTime: string;
  hasAddress: boolean;
  addressLine1: string;
  addressLine2?: string;
  cityStateZip: string;
  providerName: string;
}

export const appointmentConfirmationViewModel = (userData: UserData): AppointmentConfirmationViewModel => {
  const homeAddress = userData.homeAddress;
  const providerProfile = userData.providerProfile;

  return {
    formattedDateTime: userData.timeSlot ? formatAppointmentDateTime(userData.timeSlot) : '',
    hasAddress: !!homeAddress?.address_1,
    addressLine1: homeAddress?.address_1 || 'Address Line 1 Unavailable',
    addressLine2: homeAddress?.address_2,
    cityStateZip: `${homeAddress?.city || 'City Unavailable'}, ${homeAddress?.state || 'State Unavailable'} ${
      homeAddress?.zip_code || 'Zip Code Unavailable'
    }`,
    providerName: providerProfile
      ? `${providerProfile.firstName} ${providerProfile.lastName}, ${providerProfile.credentials}`
      : 'Provider Unavailable',
  };
};

const HomeAddressView = ({ viewModel }: { viewModel: AppointmentConfirmationViewModel }) => {
  return viewModel.hasAddress ? (
    <div className="text-center mb-4">
      <>
        {viewModel.addressLine1} <br />
        {viewModel.addressLine2 && (
          <>
            {viewModel.addressLine2} <br />
          </>
        )}
        {viewModel.cityStateZip}
      </>
    </div>
  ) : null;
};

interface AppointmentConfirmationProps {
  viewModel: AppointmentConfirmationViewModel;
  handleNext: () => Promise<void>;
  isLoading: boolean;
}

const AppointmentConfirmation = ({ viewModel, handleNext, isLoading }: AppointmentConfirmationProps) => {
  return (
    <Container>
      <Heading2>You're Almost Finished!</Heading2>
      <p className="text-gray-700 mb-6 text-center">
        Take a moment to review your appointment information below. If everything looks correct, click{' '}
        <span className="font-bold">Book My Appointment</span> below. Once you finalize your booking, you'll receive an
        email and SMS confirmation.
      </p>

      <div className="mb-6 appointment-confirmation">
        <p className="text-center mb-4">
          <span className="bg-clique-yellow px-2 py-1 text-xl font-semibold">{viewModel.formattedDateTime}</span>
        </p>
        <HomeAddressView viewModel={viewModel} />
        <div className="text-center mb-4">
          <p className="text-lg font-semibold">Provider:</p>
          <p>{viewModel.providerName}</p>
        </div>
      </div>

      <div className="flex justify-center mt-8">
        <FormActionButton onClick={handleNext} isLoading={isLoading}>
          Book My Appointment
        </FormActionButton>
      </div>
    </Container>
  );
};

export default AppointmentConfirmation;
